import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component } from '@angular/core';
import { ControlContainer, FormsModule, NgForm } from '@angular/forms';
import { ScrollToService } from '@df/dom/scroll/scroll-to.service';
import { V12RetailFinanceComponentDef } from '@df/module-V12/v12-retail-finance.component.def';
import { GridModule } from '@df/ui/atomic/atom/grid/grid.module';
import { PriceComponent } from '@df/ui/price/price.component';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { CheckoutFormComponent } from '../../checkout/form/checkout-form.component';
import { CheckoutStepComponent } from '../../checkout/step/checkout-step.component';
import { V12RetailFinanceInfoComponent } from './info/v12-retail-finance-info.component';
interface IV12RetailFinanceInfoItem {
    title: string;
    value: number | string;
    isPrice?: boolean;
}
@Component({
    selector: 'v12-retail-finance',
    templateUrl: './v12-retail-finance.component.html',
    styleUrl: './v12-retail-finance.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
    standalone: true,
    imports: [
        CommonModule,
        GridModule,
        V12RetailFinanceInfoComponent,
        PriceComponent,
        TranslationModule,
        FormsModule
    ]
})
export class V12RetailFinanceComponent extends V12RetailFinanceComponentDef {
    constructor(
        checkoutFormComponent: CheckoutFormComponent,
        _ref: ChangeDetectorRef,
        protected override scrollToService: ScrollToService,
        checkoutStepcomponent: CheckoutStepComponent
    ) {
        super(checkoutFormComponent, _ref, scrollToService, checkoutStepcomponent);
    }

    /**
     * Calculate Annual Rate of Interest
     * Formula: annualRate = (((interest / loanAmount) * 100) / ((months + deferredPeriod) / 12))
     * Note: deferredPeriod is always 0
     */
    get annualRateOfInterest(): number {
        if (!this.info) return 0;

        const { loanAmount, creditAmount, terms } = this.info;

        if (loanAmount <= 0 || terms <= 0) return 0;

        const interest = creditAmount;
        const annualRate = (((interest / loanAmount) * 100) / (terms / 12));

        return annualRate;
    }

    get infoItems(): IV12RetailFinanceInfoItem[] {
        if (this.info) {
            const {
                productName,
                totalAmount,
                depositAmount,
                loanAmount,
                interestRate,
                monthlyPayment,
                terms,
                creditAmount,
                totalAmountPayable
            } = this.info;
            return [
                { title: 'Product Name', value: productName },
                { title: 'Cash Price', value: totalAmount, isPrice: true },
                { title: 'Amount of Deposit', value: depositAmount, isPrice: true },
                { title: 'Total Amount of Credit', value: loanAmount, isPrice: true },
                { title: 'Representative APR %', value: `${interestRate}%` },
                { title: 'Annual Rate of Interest (Fixed)', value: `${this.annualRateOfInterest.toFixed(2)}%` },
                { title: 'Monthly Repayment', value: monthlyPayment, isPrice: true },
                { title: 'Loan Term', value: terms },
                { title: 'Total Cost of Credit', value: creditAmount, isPrice: true },
                { title: 'Total Amount Payable', value: totalAmountPayable, isPrice: true }
            ];
        }
        return [];
    }

    trackByInfoItemTitleValue(index: number, infoItem: IV12RetailFinanceInfoItem): string {
        return `${infoItem.title}_${infoItem.value}`;
    }

    override ngAfterViewInit(): void {
        super.ngAfterViewInit();
        let defaultOption = this.packOptions.find(packOption => packOption.months === 48);
        if (defaultOption) {
            this.packOptionId = defaultOption.id;
        }
    }

    asNumber(value: number | string): number {
        return typeof value === 'string' ? parseInt(value, 10) : value;
    }

    getMonthlyPriceForPackOption(option: any): number {
        const totalAmount = this.info?.totalAmount || 0;
        const loanAmount = totalAmount - (totalAmount / 100) * 10;
        return Math.round(loanAmount * option.interest * 100) / 100;
    }

    selectPackOption(packOptionId: string) {
        this.packOptionId = packOptionId;
    }
}
